import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';
import { useGeolocation } from '@/contexts/GeolocationContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from './use-toast';

/**
 * Smart Language Management Hook
 * Handles language preference with priority order:
 * 1. Logged-in user's saved preference (highest priority)
 * 2. Geo-IP detection for logged-out users
 * 3. Default to English (fallback)
 */
export function useSmartLanguage() {
  const { i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { suggestedLanguage, loading: geoLoading } = useGeolocation();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language on mount
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let mounted = true;

    const initializeLanguage = async () => {
      // Only block on auth loading, geo can update in background
      if (authLoading) {
        return;
      }

      // Clear any existing timeout when we start initialization
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      try {
        // Priority 1: Logged-in user's saved preference
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_language')
            .eq('id', user.id)
            .single();

          if (mounted && profile?.preferred_language) {
            await i18n.changeLanguage(profile.preferred_language);
            setIsInitialized(true);
            return;
          }
        }

        // Priority 2: Geo-IP detection (only if user hasn't manually picked before)
        const manualPick = typeof window !== 'undefined'
          ? localStorage.getItem('aicavite_lang_manual')
          : null;
        if (
          mounted &&
          !manualPick &&
          suggestedLanguage &&
          suggestedLanguage !== i18n.language
        ) {
          await i18n.changeLanguage(suggestedLanguage);
        } else if (mounted && manualPick && manualPick !== i18n.language) {
          await i18n.changeLanguage(manualPick);
        }

        if (mounted) {
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Failed to initialize language:', error);
        if (mounted) {
          await i18n.changeLanguage('en');
          setIsInitialized(true);
        }
      }
    };

    // Set timeout for forced initialization
    if (!isInitialized && !authLoading) {
      timeoutId = setTimeout(() => {
        if (mounted && !isInitialized) {
          console.warn('Language initialization timeout - using default language');
          i18n.changeLanguage('en');
          setIsInitialized(true);
        }
      }, 2000);
    }

    initializeLanguage();

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, authLoading, geoLoading, suggestedLanguage, i18n, isInitialized]);

  // Save language preference for logged-in users
  const changeLanguage = async (newLang: 'en' | 'pt-BR') => {
    try {
      await i18n.changeLanguage(newLang);
      try { localStorage.setItem('aicavite_lang_manual', newLang); } catch {/* ignore */}

      if (user) {
        const { error } = await supabase
          .from('profiles')
          .update({ preferred_language: newLang })
          .eq('id', user.id);

        if (error) throw error;

        toast({
          title: "Language Updated",
          description: `Language changed to ${newLang === 'en' ? 'English' : 'Português'}`,
        });
      }
    } catch (error) {
      console.error('Failed to change language:', error);
      toast({
        title: "Error",
        description: "Failed to save language preference",
        variant: "destructive",
      });
    }
  };

  return {
    currentLanguage: i18n.language as 'en' | 'pt-BR',
    changeLanguage,
    isInitialized,
    suggestedLanguage,
  };
}
